import React, { useState, useEffect, useCallback, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const AUTOPLAY_MS = 10000;
const PUBLIC = process.env.PUBLIC_URL || "";

function useWindowWidth() {
  const [w, setW] = useState(typeof window !== "undefined" ? window.innerWidth : 1200);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return w;
}

export default function PartnerSlider() {
  const [partners, setPartners] = useState([]);
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const timerRef = useRef(null);
  const trackRef = useRef(null);
  const width = useWindowWidth();

  useEffect(() => {
    fetch(`${PUBLIC}/partners/manifest.json`)
      .then((r) => r.json())
      .then((data) => { if (Array.isArray(data) && data.length) setPartners(data); })
      .catch(() => {});
  }, []);

  const count = partners.length;
  const visible = width >= 1024 ? 3 : width >= 640 ? 2 : 1;

  // Clone: [clones of last 'visible' items] + [originals] + [clones of first 'visible' items]
  const extendedLogos = count > 0
    ? [...partners.slice(-visible), ...partners, ...partners.slice(0, visible)]
    : [];
  const offsetIndex = current + visible; // shift by the prepended clones

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (count <= visible) return;
    timerRef.current = setInterval(() => {
      setCurrent((p) => p + 1);
      setIsTransitioning(true);
    }, AUTOPLAY_MS);
  }, [count, visible]);

  useEffect(() => {
    resetTimer();
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [resetTimer]);

  // Seamless loop: snap back without animation when reaching clone boundaries
  useEffect(() => {
    if (!isTransitioning || count <= visible) return;

    const handleTransitionEnd = () => {
      if (current >= count) {
        setIsTransitioning(false);
        setCurrent(0);
      } else if (current < 0) {
        setIsTransitioning(false);
        setCurrent(count - 1);
      }
    };

    const track = trackRef.current;
    if (track) {
      track.addEventListener("transitionend", handleTransitionEnd);
      return () => track.removeEventListener("transitionend", handleTransitionEnd);
    }
  }, [current, count, visible, isTransitioning]);

  // Re-enable transition after snap
  useEffect(() => {
    if (!isTransitioning) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsTransitioning(true);
        });
      });
    }
  }, [isTransitioning]);

  const go = (dir) => {
    setCurrent((p) => p + dir);
    setIsTransitioning(true);
    resetTimer();
  };

  if (!count) return null;

  const slideWidth = 100 / extendedLogos.length;
  const translateX = offsetIndex * slideWidth;

  return (
    <section className="relative py-16 bg-slate-50" data-testid="partners-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1.5 bg-cyan-500/10 border border-cyan-500/20 rounded-full text-cyan-600 text-sm font-medium mb-4">
            Our Partners
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-800">
            Trusted <span className="bg-gradient-to-r from-cyan-500 to-teal-500 bg-clip-text text-transparent">Partners</span>
          </h2>
        </div>

        <div className="relative flex items-center">
          {/* Left Arrow */}
          <button
            onClick={() => go(-1)}
            data-testid="partner-prev-btn"
            aria-label="Previous partner"
            className="absolute left-0 z-10 w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-white border border-slate-200 shadow-md flex items-center justify-center text-slate-500 hover:text-cyan-500 hover:border-cyan-300 hover:shadow-lg transition-all duration-300 -translate-x-1 sm:-translate-x-5"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          {/* Slider Track */}
          <div className="overflow-hidden mx-10 sm:mx-14 w-full">
            <div
              ref={trackRef}
              className="flex"
              style={{
                width: `${(extendedLogos.length / visible) * 100}%`,
                transform: `translateX(-${translateX}%)`,
                transition: isTransitioning ? "transform 700ms ease-in-out" : "none",
              }}
            >
              {extendedLogos.map((p, i) => (
                <div
                  key={`${p.file}-${i}`}
                  className="flex-shrink-0 px-2 sm:px-3"
                  style={{ width: `${100 / extendedLogos.length}%` }}
                >
                  <div className="flex items-center justify-center h-14 sm:h-16 lg:h-20">
                    <img
                      src={`${PUBLIC}/partners/${p.file}`}
                      alt={p.name}
                      data-testid={`partner-logo-${p.name?.toLowerCase()}`}
                      loading="lazy"
                      decoding="async"
                      className="max-h-10 sm:max-h-12 lg:max-h-16 max-w-[90%] object-contain select-none"
                      draggable={false}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Arrow */}
          <button
            onClick={() => go(1)}
            data-testid="partner-next-btn"
            aria-label="Next partner"
            className="absolute right-0 z-10 w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-white border border-slate-200 shadow-md flex items-center justify-center text-slate-500 hover:text-cyan-500 hover:border-cyan-300 hover:shadow-lg transition-all duration-300 translate-x-1 sm:translate-x-5"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Dots */}
        {count > visible && (
          <div className="flex justify-center gap-2 mt-8">
            {partners.map((_, i) => (
              <button
                key={i}
                onClick={() => { setCurrent(i); setIsTransitioning(true); resetTimer(); }}
                aria-label={`Go to slide ${i + 1}`}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  ((current % count) + count) % count === i ? "bg-cyan-500 w-6" : "bg-slate-300 hover:bg-slate-400"
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
